import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import { Document } from 'document/document';
import { DocumentI18n } from 'document/document-i18n';
import { DocumentType } from 'document/model/document.type';
import { PhaseApplicationPort } from 'phase/port/phase-application.port';
import { SectionRepositoryPort } from 'phase/section/port/section-repository.port';

import { AppLocator } from '../../app.locator';

export interface DocumentI18nDocument {
	readonly url: string;
	readonly name: string;
	readonly locale: string;
}

export interface DocumentDocument {
	readonly id: string;
	readonly type: DocumentType;
	readonly ownerId: string;
	readonly phaseId: string;
	readonly moduleId: string;
	readonly sectionId: string;
	readonly updateTimestamp: number;
	readonly creationTimestamp: number;
	readonly localizations: DocumentI18nDocument[];
}

export type DocumentState = EntityState<DocumentDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'documents' })
export class DocumentStore extends EntityStore<DocumentState> {
	constructor() {
		super();
	}

	public get _translateService(): TranslateService {
		return AppLocator.inject(TranslateService);
	}

	public get _sectionRepositoryPort(): SectionRepositoryPort {
		return AppLocator.inject(SectionRepositoryPort);
	}

	public asDocument(document: Document): DocumentDocument {
		return document
			? {
					id: document.id,
					ownerId: document.ownerId,
					updateTimestamp: document.updateTimestamp,
					creationTimestamp: document.creationTimestamp,
					localizations: document.localizations.map((loc) => ({
						locale: loc.locale,
						name: loc.name,
						url: loc.url,
					})),
					sectionId: document.sectionId,
					moduleId: document.moduleId,
					phaseId: document.phaseId,
					type: document.type,
			  }
			: null;
	}

	public asEntity(document: DocumentDocument): Document {
		const localizations = !!document
			? document.localizations.map(
					(loc) => new DocumentI18n(loc.url, loc.name, loc.locale)
			  )
			: [];

		return document
			? new Document(
					document.id,
					document.ownerId,
					document.type,
					document.phaseId,
					document.moduleId,
					document.sectionId,
					document.updateTimestamp,
					document.creationTimestamp,
					localizations,
					this._observeLocalization(localizations),
					document.sectionId
						? this._sectionRepositoryPort.observeSection(document.sectionId)
						: of(null)
			  )
			: null;
	}

	private _observeLocalization<T extends { locale: string }>(
		localizations: ReadonlyArray<T>
	): Observable<T> {
		return localizations && localizations.length > 0
			? defer(() =>
					this._translateService.onLangChange.pipe(
						map((e) => e.lang),
						startWith(this._translateService.currentLang)
					)
			  ).pipe(
					map((lang) => localizations.find((loc) => loc.locale === lang)),
					map((loc) => (!loc ? localizations[0] : loc)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}
}
